import { Controller } from "@hotwired/stimulus"
import {
  buildTooltipFor,
  hideAllTooltips,
  hideTooltip,
  positionTooltip,
} from "../utils/plantafel_tooltip"

// Tracks which controller instance should currently show a tooltip.
// Prevents race conditions when rapidly moving between elements.
let activeTooltipInstance = null

// Controls tooltip display for Plantafel conflict indicators
// Supports two modes:
// 1. Data-attribute mode: builds tooltip HTML dynamically from JSON data on hover (resource panel)
// 2. Legacy inline mode: uses pre-rendered tooltip HTML in the DOM (task bars, other components)
// Moves tooltip to body to escape all stacking contexts
//
// This per-node controller is for Plantafel proper (and other low-density surfaces). The
// capacity-planning boards render thousands of tooltip-bearing nodes, so they use ONE delegated
// `planning-tooltip-delegate` per surface instead — sharing the rendering via utils/plantafel_tooltip.
export default class extends Controller {
  connect() {
    this.boundShow = this.show.bind(this)
    this.boundHide = this.hide.bind(this)
    this.dynamicTooltip = null
    this.movedToBody = false
    this.originalParent = null
    this.showTimeout = null

    this.element.addEventListener("mouseenter", this.boundShow)
    this.element.addEventListener("mouseleave", this.boundHide)
  }

  disconnect() {
    this.element.removeEventListener("mouseenter", this.boundShow)
    this.element.removeEventListener("mouseleave", this.boundHide)

    if (activeTooltipInstance === this) {
      activeTooltipInstance = null
    }

    // Clean up dynamic tooltip
    if (this.dynamicTooltip) {
      this.dynamicTooltip.remove()
      this.dynamicTooltip = null
    }

    // Return legacy tooltip to original parent if it was moved
    if (this.movedToBody && this.originalParent) {
      const tooltip = this.legacyTooltip
      if (tooltip) {
        try {
          tooltip.classList.add("hidden")
          this.originalParent.appendChild(tooltip)
        } catch (_e) {
          tooltip.remove()
        }
      }
      this.movedToBody = false
    }
  }

  get useDynamicMode() {
    return !!this.element.dataset.plantafelTooltipDataValue
  }

  get legacyTooltip() {
    const tooltipId = this.element.dataset.plantafelTooltipIdValue
    if (tooltipId) {
      return document.getElementById(`${tooltipId}-tooltip`)
    }
    return this.element.querySelector('[data-plantafel-tooltip-target="tooltip"]')
  }

  get activeTooltip() {
    if (this.useDynamicMode) {
      if (!this.dynamicTooltip) {
        this.dynamicTooltip = buildTooltipFor(this.element)
      }
      return this.dynamicTooltip
    }
    return this.legacyTooltip
  }

  show() {
    // Cancel any other controller's pending show
    if (activeTooltipInstance && activeTooltipInstance !== this) {
      activeTooltipInstance.hide()
    }
    activeTooltipInstance = this

    // Clear any pending show timeout on this instance
    if (this.showTimeout) {
      clearTimeout(this.showTimeout)
    }

    // FIRST: Hide all other tooltips immediately
    hideAllTooltips()

    // Small delay to debounce rapid mouse movements
    this.showTimeout = setTimeout(() => {
      this.showTooltip()
    }, 150)
  }

  showTooltip() {
    // Verify this is still the active tooltip instance
    if (activeTooltipInstance !== this) return
    // Double-check element is still hovered
    if (!this.element.matches(":hover")) return

    // Hide any lingering tooltips before showing this one
    hideAllTooltips()

    const tooltip = this.activeTooltip
    if (!tooltip) return

    // For legacy mode, move tooltip to body to escape stacking contexts
    if (!this.useDynamicMode && !this.movedToBody) {
      this.originalParent = tooltip.parentNode
      document.body.appendChild(tooltip)
      this.movedToBody = true
    }

    positionTooltip(tooltip, this.element)
  }

  hide() {
    // Cancel any pending show
    if (this.showTimeout) {
      clearTimeout(this.showTimeout)
      this.showTimeout = null
    }

    // Release active instance tracking
    if (activeTooltipInstance === this) {
      activeTooltipInstance = null
    }

    const tooltip = this.useDynamicMode ? this.dynamicTooltip : this.legacyTooltip
    if (tooltip) {
      hideTooltip(tooltip)
    }
  }
}
